class StoreFinder {
    constructor() {
        const root = document.querySelector('[is=storeFinder]');
        const trigger = document.querySelector('.storeFinder-trigger');
        if (!root || !trigger) return;

        this.el = {
            root,
            trigger,
            closeBtn: root.querySelector('.close'),
            overlay: root.querySelector('.overlay'),
            countrySelect: root.querySelector('.country-select'),
            countrySelector: root.querySelector('.country-selector'),
            selectedCountry: root.querySelector('.selected-country'),
            shopItems: root.querySelectorAll('.shop-item'),
            emptyState: root.querySelector('.empty-state'),
            locationInfo: root.querySelector('.location-info')
        };

        this.state = {
            isOpen: false,
            userLocation: null,
            countries: new Set([...this.el.shopItems].map(i => i.dataset.country).filter(Boolean))
        };

        this.init();
    }

    init() {
        this.el.root.addEventListener('click', e => {
            if (e.target === this.el.overlay || e.target.closest('.close')) this.close();
        });

        this.el.trigger.addEventListener('click', () => this.open());
        this.el.countrySelect?.addEventListener('change', e => this.setCountry(e.target.value));
        document.addEventListener('keydown', e => this.state.isOpen && e.key === 'Escape' && this.close());

        if (this.state.countries.size) this.populateCountrySelect();
        this.hideAllShops();

        const granted = sessionStorage.getItem('userLocationGranted');
        const lat = sessionStorage.getItem('userLat');
        const lon = sessionStorage.getItem('userLon');

        if (granted === 'true' && lat && lon) {
            this.reverseGeocode(lat, lon);
        } else if (granted !== 'false') {
            this.detectLocation();
        } else {
            this.setCountry('DE');
        }
    }

    populateCountrySelect() {
        if (!this.el.countrySelect) return;
        const options = ['<option value="">Select Country</option>'];
        [...this.state.countries].sort().forEach(c => options.push(`<option value="${c}">${c}</option>`));
        this.el.countrySelect.innerHTML = options.join('');
    }

    async detectLocation() {
        if (!navigator.geolocation) {
            this.setCountry('DE');
            return;
        }

        try {
            const position = await new Promise((resolve, reject) =>
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
            );
            const { latitude, longitude } = position.coords;
            sessionStorage.setItem('userLocationGranted', 'true');
            sessionStorage.setItem('userLat', latitude);
            sessionStorage.setItem('userLon', longitude);
            await this.reverseGeocode(latitude, longitude);
        } catch (err) {
            sessionStorage.setItem('userLocationGranted', 'false');
            this.setCountry('DE');
        }
    }

    async reverseGeocode(lat, lon) {
        try {
            const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=3&addressdetails=1`;
            const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
            if (!response.ok) throw new Error();
            const data = await response.json();
            const country = data.address?.country_code?.toUpperCase();

            if (country && this.state.countries.has(country)) {
                this.setCountry(country);
            } else if (country === 'DE' || !country) {
                this.setCountry('DE');
            } else {
                this.showCountrySelector();
                this.toggleUIForCountry(country);
            }
        } catch (err) {
            this.setCountry('DE');
        }
    }

    setCountry(code) {
        if (!code) return this.resetSelection();
        this.state.userLocation = code;
        this.el.selectedCountry.textContent = code;
        this.el.countrySelect.value = code;
        this.filterShops(code);
        this.showCountrySelector();
        this.el.locationInfo.style.display = 'none';
        this.toggleUIForCountry(code);
    }

    toggleUIForCountry(countryCode) {
        const isEnglishPage = window.location.pathname.includes('/en/');

        window.dispatchEvent(new CustomEvent('storefinder:country-detected', {
            detail: {
                country: countryCode
            }
        }));

        if (isEnglishPage || countryCode !== 'DE') {
            this.showStoreFinder();
        } else if (countryCode === 'DE') {
            this.showAddToCart();
        } else {
            this.showStoreFinder();
        }
    }

    showAddToCart() {
        const addToCartSection = document.getElementById('add-to-cart-section');
        const storeFinderSection = document.getElementById('store-finder-section');

        if (addToCartSection) addToCartSection.style.display = '';
        if (storeFinderSection) storeFinderSection.style.display = 'none';
    }

    showStoreFinder() {
        const addToCartSection = document.getElementById('add-to-cart-section');
        const storeFinderSection = document.getElementById('store-finder-section');

        if (addToCartSection) addToCartSection.style.display = 'none';
        if (storeFinderSection) storeFinderSection.style.display = '';
    }

    filterShops(code) {
        let count = 0;
        this.el.shopItems.forEach(item => {
            const show = item.dataset.country === code;
            item.style.display = show ? '' : 'none';
            if (show) count++;
        });
        this.el.emptyState.style.display = count ? 'none' : 'block';
    }

    showCountrySelector() {
        this.el.countrySelector.style.display = 'block';
        if (sessionStorage.getItem('userLocationGranted') === 'false' && !this.state.userLocation) {
            this.el.locationInfo.style.display = 'block';
        }
    }

    hideAllShops() {
        this.el.shopItems.forEach(item => item.style.display = 'none');
        this.el.emptyState.style.display = 'none';
    }

    resetSelection() {
        this.hideAllShops();
        this.el.selectedCountry.textContent = '';
        this.state.userLocation = null;
        this.toggleUIForCountry(null);
    }

    open() {
        this.state.isOpen = true;
        this.el.root.setAttribute('aria-hidden', 'false');
        this.el.trigger.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.state.userLocation ? this.filterShops(this.state.userLocation) : this.hideAllShops();
        this.showCountrySelector();
        requestAnimationFrame(() => this.el.closeBtn?.focus());
    }

    close() {
        this.state.isOpen = false;
        this.el.root.setAttribute('aria-hidden', 'true');
        this.el.trigger.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        this.el.trigger.focus();
    }
}

(() => {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new StoreFinder());
    } else {
        new StoreFinder();
    }
})();